import React, { useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import DateRangeIcon from '@material-ui/icons/DateRange';

const DEFAULT_WINDOW_PADDING_DAYS = 7;

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  },
  dialogContent: {
    padding: theme.spacing(2.5),
  },
  chartWrapper: {
    backgroundColor: '#fff',
    padding: theme.spacing(1),
    height: 360,
    position: 'relative',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
}));

/**
 * @function TideChartModal
 * @description Dialog showing a Chart.js line chart of the full recorded tide series for the
 * user-provided period, with a marker for every satellite image whose waterline was extracted —
 * positioned at that image's actual acquisition date and the tide level matched to it. Mirrors
 * Bathymetry's own TideChartModal, but uses the project's already-installed Chart.js 2.x (via
 * `react-chartjs-2`, same as EvolutionChart.jsx) instead of Bathymetry's CDN-loaded Chart.js 4 —
 * no external network dependency, so it renders the same regardless of the machine's CDN access.
 * Opens by default zoomed to the acquisition dates' span plus/minus `DEFAULT_WINDOW_PADDING_DAYS`,
 * since the full tide record can span years while the interesting part is always right around the
 * images — a toggle button switches to the full, unbounded timeline and back.
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility.
 * @param {Function} props.onClose - Called when the dialog should close.
 * @param {Array<{date: string, level: number}>} props.tideSeries - Full recorded tide series
 * (`date` as `'YYYY-MM-DD HH:mm:ss'`, matching the uploaded CSV's own format).
 * @param {Array<{date: string, tideLevel: number}>} props.acquisitionPoints - One entry per
 * processed satellite image: its acquisition date (ISO) and the tide level matched to it.
 * **Called by:
 * @see {ProcessingIntertidalPage} - Intertidal's result page, via the "view tide chart" button.
 * @memberof module:Intertidal
 * @returns {JSX.Element}
 */
const TideChartModal = ({ open, onClose, tideSeries, acquisitionPoints }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const exportRef = useRef();
  const [showFullTimeline, setShowFullTimeline] = useState(false);

  const charLanguage = Boolean(i18n.language?.toLowerCase().startsWith('pt'));
  moment.locale(charLanguage ? 'pt-BR' : 'en');

  const tidePoints = useMemo(
    () =>
      (tideSeries || [])
        .map((entry) => ({ x: moment(entry.date, 'YYYY-MM-DD HH:mm:ss'), y: entry.level }))
        .filter((p) => p.x.isValid() && Number.isFinite(p.y))
        .sort((a, b) => a.x - b.x),
    [tideSeries],
  );

  const acquisitionData = useMemo(
    () =>
      (acquisitionPoints || [])
        .map((entry) => ({ x: moment(entry.date), y: entry.tideLevel }))
        .filter((p) => p.x.isValid() && Number.isFinite(p.y)),
    [acquisitionPoints],
  );

  const windowRange = useMemo(() => {
    if (!acquisitionData.length) return null;
    const times = acquisitionData.map((p) => p.x.valueOf());
    return {
      min: moment(Math.min(...times)).subtract(DEFAULT_WINDOW_PADDING_DAYS, 'days'),
      max: moment(Math.max(...times)).add(DEFAULT_WINDOW_PADDING_DAYS, 'days'),
    };
  }, [acquisitionData]);

  const levels = tidePoints.map((p) => p.y);
  const seriesMin = levels.length ? Math.min(...levels).toFixed(2) : '—';
  const seriesMax = levels.length ? Math.max(...levels).toFixed(2) : '—';

  const data = {
    datasets: [
      {
        label: t('forms.intertidal.tideChart.seriesLabel', { min: seriesMin, max: seriesMax }),
        data: tidePoints,
        borderColor: 'rgba(130,130,130,0.6)',
        backgroundColor: 'rgba(130,130,130,0.6)',
        borderWidth: 1,
        pointRadius: 0,
        fill: false,
        lineTension: 0,
      },
      {
        label: t('forms.intertidal.tideChart.acquisitionLabel', { count: acquisitionData.length }),
        data: acquisitionData,
        showLine: false,
        borderColor: '#c0392b',
        backgroundColor: '#c0392b',
        pointRadius: 6,
        pointStyle: 'crossRot',
        pointBorderWidth: 2,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    animation: { duration: 0 },
    scales: {
      xAxes: [
        {
          type: 'time',
          time: { tooltipFormat: charLanguage ? 'DD/MM/YYYY HH:mm' : 'MM/DD/YYYY HH:mm' },
          scaleLabel: { display: true, labelString: t('forms.intertidal.tideChart.xAxisLabel') },
          ticks:
            !showFullTimeline && windowRange
              ? { min: windowRange.min, max: windowRange.max }
              : {},
        },
      ],
      yAxes: [
        {
          scaleLabel: { display: true, labelString: t('forms.intertidal.tideChart.yAxisLabel') },
          ticks: {
            callback: (value) => (charLanguage ? value.toFixed(2).replace('.', ',') : value.toFixed(2)),
          },
        },
      ],
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, chartData) => {
          const label = chartData.datasets[tooltipItem.datasetIndex].label || '';
          let val = parseFloat(tooltipItem.yLabel).toFixed(2);
          if (charLanguage) val = val.replace('.', ',');
          return `${label.split(' (')[0]}: ${val}`;
        },
      },
    },
  };

  /**
   * @function handleExportImage
   * @description Renders the chart to a PNG and triggers its download. Labels come straight from
   * `t(...)`, so the exported image always matches whichever language is currently active in the
   * app — no separate language picker.
   * **Called by:
   * @see {TideChartModal} - via the "export" button's onClick.
   * @memberof module:Intertidal
   * @returns {Promise<void>}
   */
  const handleExportImage = async () => {
    if (!exportRef.current) return;
    const canvas = await html2canvas(exportRef.current);
    const link = document.createElement('a');
    link.download = 'grafico-mare.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {t('forms.intertidal.tideChart.dialogTitle')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {tidePoints.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {t('forms.intertidal.tideChart.noData')}
          </Typography>
        ) : (
          <>
            <div ref={exportRef} className={classes.chartWrapper}>
              <Line key={showFullTimeline ? 'full' : 'limited'} data={data} options={options} />
            </div>
            <div className={classes.footer}>
              {windowRange && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DateRangeIcon />}
                  onClick={() => setShowFullTimeline((prev) => !prev)}>
                  {showFullTimeline
                    ? t('forms.intertidal.tideChart.showLimitedRange', { days: DEFAULT_WINDOW_PADDING_DAYS })
                    : t('forms.intertidal.tideChart.showFullTimeline')}
                </Button>
              )}
              <Button variant="contained" color="primary" startIcon={<GetAppIcon />} onClick={handleExportImage}>
                {t('forms.intertidal.tideChart.exportButton')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TideChartModal;
